import { HTMLAttributes, useMemo } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Typography from '@/components/base/Typography'
import TypographyMask from '@/components/base/TypographyMask'

import { opacityReveal } from './animations/opacity'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type OnlyTextProps = HTMLAttributes<HTMLElement> &
  Partial<{
    pretitle: string
    title: string
    description: string
    link: WPLink
    alignment: 'left' | 'center' | 'right'
    layout: 'row' | 'column'
  }>

const OnlyText = ({
  pretitle,
  title,
  description,
  link,
  alignment,
  layout
}: OnlyTextProps) => {
  const [isRow, isColumn, isCenter, isRight] = useMemo(
    () => [
      layout === 'row',
      layout === 'column',
      alignment === 'center',
      alignment === 'right'
    ],
    [layout, alignment]
  )

  return (
    <Motion animation={opacityReveal}>
      <section data-only-text={`${layout}_${alignment}`}>
        <Container>
          <Grid>
            <div
              className={cn('flex flex-col gap-[15px]', {
                'col-span-4 mb-[15px] md:col-span-6 lg:col-span-8': isColumn,
                'lg:px-[3vw]': isCenter,
                'col-span-4 mb-[15px] md:col-span-5 lg:col-span-8 lg:pr-[6vw]':
                  isRow,
                'text-center md:col-start-1 lg:col-start-3':
                  isCenter && isColumn,
                'md:col-start-1 lg:col-start-5': isRight && isColumn
              })}
            >
              {pretitle ? (
                <Typography variant="label">{pretitle}</Typography>
              ) : null}
              {title ? (
                <Typography variant="h3" data-only-text-title>
                  {title}
                </Typography>
              ) : null}
            </div>

            <div
              className={cn('flex flex-col justify-between', {
                'col-span-4 md:col-span-6 lg:col-span-8': isColumn,
                'lg:px-[3vw]': isCenter,
                'col-span-4 md:col-span-6 lg:col-span-4': isRow,
                'text-center md:col-start-1 lg:col-start-3':
                  isCenter && isColumn,
                'md:col-start-1 lg:col-start-5': isRight && isColumn
              })}
            >
              {description ? (
                <Typography variant="body" data-only-text-description>
                  {description}
                </Typography>
              ) : null}
              {link ? (
                <Button variant="secondary" asChild>
                  <Link
                    href={link?.url || ''}
                    className={cn('mt-[30px] self-start', {
                      'self-center': isCenter
                    })}
                    data-only-text-link
                    scroll={false}
                  >
                    <TypographyMask>
                      <span>{link.title}</span>
                    </TypographyMask>
                  </Link>
                </Button>
              ) : null}
            </div>
          </Grid>
        </Container>
      </section>
    </Motion>
  )
}

export default OnlyText
